import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["button"];

  static values = {
    createUrl: String,
    statusUrl: String,
    preparing: { type: Boolean, default: false },
    pollIntervalMs: { type: Number, default: 2000 },
    prepareText: String,
    checkingText: String,
    preparingText: String,
    readyText: String,
    errorText: String,
    failedText: String,
    expiredText: String,
  };

  connect() {
    this.busy = true;
    this.mode = "checking";
    this.epoch = 0;
    this.applyMode();
    this.syncFromServer();
  }

  disconnect() {
    this.nextEpoch();
  }

  uploadedExternally() {
    this.preparingValue = false;
    this.nextEpoch();
    this.syncFromServer();
  }

  nextEpoch() {
    this.epoch += 1;
    return this.epoch;
  }

  async handleClick(event) {
    event.preventDefault();
    if (this.busy || this.mode === "checking" || this.mode === "preparing") return;

    if (this.mode === "ready") {
      await this.downloadReady();
      return;
    }

    await this.prepareExport();
  }

  async syncFromServer() {
    const epoch = this.nextEpoch();
    this.busy = true;
    this.setMode("checking");

    try {
      const payload = await this.fetchStatusPayload();
      if (this.epoch !== epoch) return;

      const status = payload?.status;

      if (status === "pending" || status === "running") {
        this.setMode("preparing");
        const settled = await this.pollStatus(payload, epoch);
        if (!settled) return;
        this.setMode("ready");
        return;
      }

      if (status === "ready") {
        this.setMode("ready");
        return;
      }

      this.setMode("prepare");
    } catch (error) {
      if (this.epoch !== epoch) return;

      if (this.preparingValue) {
        this.setMode("preparing");
        try {
          const settled = await this.pollStatus(null, epoch);
          if (!settled) return;
          this.setMode("ready");
        } catch (pollError) {
          if (this.epoch !== epoch) return;
          window.alert(pollError?.message || this.errorTextValue || "Unable to prepare download.");
          this.setMode("prepare");
        }
        return;
      }
      this.setMode("prepare");
    } finally {
      if (this.epoch === epoch) {
        this.busy = false;
        this.applyMode();
      }
    }
  }

  async prepareExport() {
    if (this.busy) return;

    const epoch = this.nextEpoch();
    this.busy = true;
    this.setMode("preparing");

    try {
      const started = await this.startExport();
      if (this.epoch !== epoch) return;

      if (started.status === "idle") {
        throw new Error(this.errorTextValue || "Unable to start export.");
      }
      if (started.status === "ready") {
        this.setMode("ready");
        return;
      }

      const settled = await this.pollStatus({ status: started.status, job_id: started.jobId }, epoch);
      if (!settled) return;
      this.setMode("ready");
    } catch (error) {
      if (this.epoch !== epoch) return;

      window.alert(error?.message || this.errorTextValue || "Unable to prepare download.");
      this.setMode("prepare");
    } finally {
      if (this.epoch === epoch) {
        this.busy = false;
        this.applyMode();
      }
    }
  }

  async downloadReady() {
    if (this.busy) return;

    const epoch = this.epoch;
    this.busy = true;
    this.setButtonEnabled(false);

    try {
      const payload = await this.fetchStatusPayload();
      if (this.epoch !== epoch) return;

      const status = payload?.status;

      if (status === "ready") {
        await this.downloadPartsFromPayload(payload);
        this.setMode("ready");
        return;
      }

      if (status === "pending" || status === "running") {
        this.setMode("preparing");
        const settled = await this.pollStatus(payload, epoch);
        if (!settled) return;
        this.setMode("ready");
        return;
      }

      this.setMode("prepare");
      window.alert(this.expiredTextValue || this.failedTextValue || "Download is no longer available. Please prepare again.");
    } catch (error) {
      if (this.epoch !== epoch) return;

      this.setMode("prepare");
      window.alert(error?.message || this.expiredTextValue || this.errorTextValue || "Download failed.");
    } finally {
      if (this.epoch === epoch) {
        this.busy = false;
        this.applyMode();
      }
    }
  }

  async downloadPartsFromPayload(payload) {
    const urls = (payload.parts || []).map((part) => part.url).filter(Boolean);
    if (urls.length === 0) {
      throw new Error(this.failedTextValue || "Download failed.");
    }
    await this.downloadParts(urls);
  }

  async startExport() {
    const response = await fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new Error(this.errorTextValue || "Unable to start export.");
    }

    const body = await response.json();
    return { jobId: body.job_id, status: body.status };
  }

  async fetchStatusPayload() {
    const response = await fetch(this.statusUrlValue, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });

    if (response.status === 404) {
      return { status: "failed" };
    }
    if (!response.ok) {
      throw new Error(this.errorTextValue || "Unable to fetch export status.");
    }

    return response.json();
  }

  async pollStatus(seedPayload = null, epoch = this.epoch) {
    let first = true;
    let payload = seedPayload;

    while (this.epoch === epoch) {
      if (!payload || !first) {
        if (!first) await this.sleep(this.pollIntervalMsValue);
        if (this.epoch !== epoch) return null;
        payload = await this.fetchStatusPayload();
      }
      first = false;

      if (this.epoch !== epoch) return null;

      if (payload.status === "ready") return payload;
      if (payload.status === "failed") {
        throw new Error(this.failedTextValue || "Export failed.");
      }
      if (payload.status === "idle") {
        throw new Error(this.errorTextValue || "Unable to start export.");
      }
    }

    return null;
  }

  async downloadParts(urls) {
    for (let index = 0; index < urls.length; index += 1) {
      const anchor = document.createElement("a");
      anchor.href = urls[index];
      anchor.rel = "noopener";
      anchor.download = "";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      if (index < urls.length - 1) await this.sleep(300);
    }
  }

  setMode(mode) {
    this.mode = mode;
    this.applyMode();
  }

  applyMode() {
    if (!this.hasButtonTarget) return;

    const waiting = this.mode === "checking" || this.mode === "preparing";
    let label = this.prepareTextValue || "Prepare Download";
    if (this.mode === "checking") {
      label = this.checkingTextValue || "Checking…";
    } else if (this.mode === "preparing") {
      label = this.preparingTextValue || "Preparing…";
    } else if (this.mode === "ready") {
      label = this.readyTextValue || "Download All";
    }

    this.buttonTarget.textContent = label;
    this.setButtonEnabled(!waiting && !this.busy);
  }

  setButtonEnabled(enabled) {
    if (!this.hasButtonTarget) return;

    this.buttonTarget.disabled = !enabled;
    this.buttonTarget.classList.toggle("opacity-50", !enabled);
    this.buttonTarget.classList.toggle("cursor-not-allowed", !enabled);
    this.buttonTarget.classList.toggle("cursor-pointer", enabled);
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || "";
  }

  sleep(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }
}
